import type { Client } from '@/domain/schema/v1';
import { projectInstructionFull, theoryContextFile, type KnowledgeFile } from '@/domain/ai/knowledge';
import { useState } from 'react';
import { CopyButton } from './CopyButton';
import { useService } from '../hooks/useService';


/**
 * 내담자별 Claude 프로젝트 준비 (AI_WORKFLOW_PLAN_v1 §3-3): 이름·지침 복사, 지식 파일 내려받기.
 * 한 번 만들어 두면 회기마다 그 프로젝트의 새 대화에 회기 자료만 붙여넣으면 된다.
 */
async function download(f: KnowledgeFile) {
  const { saveFile } = await import('../export/docx');
  await saveFile(new Blob([f.content], { type: 'text/markdown;charset=utf-8' }), f.name);
}

export function ClaudeProjectPanel({ client }: { client: Client }) {
  const projectName = `상담｜${client.clientCode}`;
  const theory = theoryContextFile();
  return (
    <>
    {!client.externalProjectUrl && <ProjectUrlBox client={client} />}
    <details className="card">
      <summary>
        <strong>외부 AI 프로젝트 만들기</strong> <span className="muted small">이 내담자 처음 한 번</span>
      </summary>
      <ol className="stack small claude-steps">
        <li>
          새 프로젝트 창: <strong>이름</strong> 붙여넣기 <CopyButton text={projectName} label={`이름 복사 (${projectName})`} /> · 목표는 비워 둠
        </li>
        <li>
          같은 창의 <strong>컨텍스트</strong>에 기준표 파일 추가{' '}
          <button type="button" onClick={() => void download(theory)}>
            기준표 파일 받기
          </button>{' '}
          → <strong>프로젝트 생성</strong>
        </li>
        <li>
          만든 프로젝트 → 설정 → 메모리 → <strong>프로젝트 지침</strong> 칸에 붙여넣기{' '}
          <CopyButton className="primary" text={projectInstructionFull()} label="지침 복사" />
        </li>
        <li>
          만든 프로젝트의 <strong>주소</strong>를 복사해 아래에 저장 (브라우저 주소창, 또는 앱에서 공유 → 링크 복사). 저장하면 '외부로
          보내기'가 이 프로젝트를 바로 엽니다.
          <ProjectUrlBox client={client} inline />
        </li>
      </ol>
      <p className="muted small">
        지침과 기준표 파일은 모든 내담자가 같습니다 (한 번 받은 파일을 계속 쓰면 됨). 이후 회기마다: AI 분석하기 → 자료 복사 → 이 프로젝트의 새
        스레드에 붙여넣기 → 결과를 앱에 붙여넣기.
      </p>
    </details>
    </>
  );
}

/** 이 내담자의 외부 AI 프로젝트 주소 저장 */
function ProjectUrlBox({ client, inline }: { client: Client; inline?: boolean }) {
  const svc = useService();
  const [url, setUrl] = useState(client.externalProjectUrl ?? '');
  const [saved, setSaved] = useState(client.externalProjectUrl ?? '');
  const [err, setErr] = useState<string | null>(null);
  const valid = /^https:\/\/\S+$/.test(url.trim());
  const save = () =>
    void svc
      .updateClient(client.id, { externalProjectUrl: url.trim() || undefined })
      .then(() => {
        setSaved(url.trim());
        setErr(null);
      })
      .catch(() => setErr('주소를 저장하지 못했습니다. https://로 시작하는 주소인지 확인하세요.'));
  const body = (
    <div className="stack">
      <div className="row">
        <input
          aria-label="외부 AI 프로젝트 주소"
          placeholder="https://claude.ai/project/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={{ flex: '1 1 260px' }}
        />
        <button type="button" className="primary" disabled={!valid || url.trim() === saved} onClick={save}>
          {saved && url.trim() === saved ? '저장됨' : '주소 저장'}
        </button>
      </div>
      {err && <p className="warn small">{err}</p>}
    </div>
  );
  if (inline) return body;
  return (
    <div className="card stack">
      <strong className="small">상담｜{client.clientCode} 프로젝트 주소를 저장하면 '외부로 보내기'가 그 프로젝트를 바로 엽니다</strong>
      {body}
    </div>
  );
}
